import { NextPageWithLayout } from 'next';
import { AppDownloadSection } from '../../components/AppDownloadSection/AppDownloadSection';
import { Container } from '../../components/Container/Container';
import { Grid } from '../../components/Grid/Grid';
import { HeroMapSection } from '../../components/Maps/components/HeroMap/HeroMapSection';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { PageNavigation } from '../../components/Navigation/PageNavigation/PageNavigation';
import { NearbyPlacesSection } from '../../components/NearbyPlaces/NearbyPlacesSection';
import { RegulationSection } from '../../components/Regulations/RegulationSection';
import { IStateObject } from '../../constants/USstates/constants';
import { getFishingForbidden } from '../../helpers/FishingWater/getFishingForbidden';
import { getWaterPrivacyStatusFromMetadata } from '../../helpers/FishingWater/getWaterPrivacyStatusFromMetadata';
import { getEdgesFromConnection, getNodesFromConnection } from '../../helpers/graphql';
import { useIsTabletScreenWidthOrThinner } from '../../helpers/hooks/useScreenWidth';
import { getNearbyPlacesByDistance } from '../../helpers/places/getNearbyPlacesByDistance';
import { getBodyOfWaterSchema } from '../../helpers/schema';
import { getFishingWaterUrl } from '../../helpers/urls';
import {
  GetFishingWater,
  GetFishingWater_fishingWater_catches_edges_node,
  GetFishingWater_fishingWater_reviews_amenities_edges_node,
  GetFishingWaterNearbyWaters_fishingWater_suggestedWaters,
  GetFishingWaterNearestPlace_fishingWater_nearestPlace,
  GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary,
  GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary_edges,
  GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary_edges_node,
} from '../../interfaces/graphql';
import { BitetimeSection } from './components/BitetimeSection/BitetimeSection';
import { FAQSection } from './components/FAQSection/FAQSection';
import { GeneralInfoSection } from './components/GeneralInfoSection/GeneralInfoSection';
import { NearbyWatersSection } from './components/NearbyWatersSection/NearbyWatersSection';
import { NoCatchesSection } from './components/NoCatchesSection/NoCatchesSection';
import { PreFooterLinksSection } from './components/PreFooterLinksSection/PreFooterLinksSection';
import { PrivateWaterSection } from './components/PrivateWaterSection/PrivateWaterSection';
import { ReviewsSection } from './components/ReviewsSection/ReviewsSection';
import { SuggestChangesSection } from './components/SuggestChangesSection/SuggestChangesSection';
import { TopSpeciesSection } from './components/TopSpeciesSection/TopSpeciesSection';
import { getSubNavLinks, SUB_LINK_IDS } from './helpers/fishingWaterHelpers';
import { getWaterMetadataDescription } from './helpers/getWaterMetadataDescription';
import { getWaterMetadataOgTitle } from './helpers/getWaterMetadataOgTitle';
import { getWaterMetadataTitle } from './helpers/getWaterMetadataTitle';
import { FishingReportsGrid } from '../../components/FishingReportsGrid/FishingReportsGrid';
import { TrophyCatchesAppDownloadSection } from './components/TrophyCatchesAppDownloadSection/TrophyCatchesAppDownloadSection';
import ogImage from '../../../public/images/og_image_waterpages.png';

const FISHINGREPORT_SECTION_MAX_CATCHES_MOBILE = 6;
const FISHINGREPORT_SECTION_MAX_CATCHES_TABLET = 6;
const FISHINGREPORT_SECTION_MAX_CATCHES_DESKTOP = 4;
export interface IFishingWaterProps {
  data: GetFishingWater;
  nearestPlace: GetFishingWaterNearestPlace_fishingWater_nearestPlace | null;
  states: IStateObject[];
  suggestedWaters: GetFishingWaterNearbyWaters_fishingWater_suggestedWaters[];
  topSpeciesSummary: GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary | null;
}

export const FishingWater: NextPageWithLayout<IFishingWaterProps> = ({
  data,
  nearestPlace,
  states,
  suggestedWaters,
  topSpeciesSummary,
}) => {
  const {
    fishingWater: {
      _id: fishingWaterId,
      region,
      county,
      country,
      intersectingCounties,
      displayName,
      metadata,
      allReviews,
      latitude,
      longitude,
      catches,
      reviews,
      descriptionOfWaterType,
      url,
      topAnglers,
    },
  } = data;
  const isTabletScreenWidthOrThinner = useIsTabletScreenWidthOrThinner();
  const speciesEdges = getEdgesFromConnection<
    GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary_edges_node,
    GetFishingWaterTopSpecies_fishingWater_topSpeciesSummary_edges
  >(topSpeciesSummary);

  const intersectingCountiesNodes = getNodesFromConnection(intersectingCounties);

  const topSpeciesId = speciesEdges.slice(0, 1).map(({ node }) => node._id);
  const top3SpeciesNames = speciesEdges.slice(0, 3).map(({ node }) => node.displayName);

  const countryName = country?.countryName;
  const countryId = country?.id;
  const regionCode = countryId === 'US' ? (region?.id ?? region?.name) : region?.name;
  const regionName = region?.name;

  const nearbyPlaces = nearestPlace?.nearbyPlaces;
  const nearbyPlacesNodes = getNodesFromConnection(nearbyPlaces);
  const nearbyPlacesDistanceUnit = getEdgesFromConnection(nearbyPlaces)[0]?.distanceUnit;
  const nearbyPlacesArray = getNearbyPlacesByDistance({
    nearbyPlaces: nearestPlace ? [nearestPlace, ...nearbyPlacesNodes] : [],
    distanceUnit: nearbyPlacesDistanceUnit,
    mainLatitude: latitude,
    mainLongitude: longitude,
  });
  const waterPrivacyStatus = getWaterPrivacyStatusFromMetadata(metadata);
  const fishingForbidden = getFishingForbidden(waterPrivacyStatus);
  const showRegulations = country?.id.toLowerCase() === 'us';

  const amenitiesList =
    getNodesFromConnection<GetFishingWater_fishingWater_reviews_amenities_edges_node>(
      reviews?.amenities,
    );

  const recentCatchPostList =
    getNodesFromConnection<GetFishingWater_fishingWater_catches_edges_node>(catches).map(
      _catch => _catch.post,
    );

  const catchCount = recentCatchPostList.length;
  const topSpeciesCount = topSpeciesSummary?.edges?.length ?? 0;

  const hasCatches = !fishingForbidden && catchCount > 0;
  const hasReviews = !fishingForbidden && allReviews && allReviews.totalCount > 0;
  const hasGeneralInfo = isTabletScreenWidthOrThinner;
  const hasSpecies = !fishingForbidden && topSpeciesCount > 0;
  const hasNearbyWaters = suggestedWaters.length > 0;
  const hasRegulations = !fishingForbidden && showRegulations;
  const showNoCatchesSection = !hasCatches || !hasSpecies;

  return (
    <div>
      <MetaTags
        link={getFishingWaterUrl(fishingWaterId, displayName)}
        title={getWaterMetadataTitle(displayName, nearestPlace?.name, regionCode, countryName)}
        ogTitle={getWaterMetadataOgTitle(displayName, regionCode, countryName)}
        description={getWaterMetadataDescription(
          displayName,
          nearestPlace?.name,
          regionName,
          regionCode,
          countryName,
        )}
        schema={getBodyOfWaterSchema({
          externalId: fishingWaterId,
          latitude,
          longitude,
          name: displayName,
        })}
        image={ogImage.src}
      />
      <PageNavigation
        navItems={getSubNavLinks({
          hasCatches,
          hasGeneralInfo,
          hasReviews: hasReviews ?? false,
          hasRegulations,
          hasSpecies,
          hasNearbyWaters,
        })}
      />
      {/* Map Section */}
      <HeroMapSection
        country={country}
        region={region}
        id={SUB_LINK_IDS.MAP}
        longitude={longitude}
        latitude={latitude}
        title={displayName}
        mainFishingWater={data.fishingWater}
        reviewSectionId={SUB_LINK_IDS.REVIEWS}
        fishingReportSectionId={SUB_LINK_IDS.FISHING_REPORTS}
        SEOFishingSpots={suggestedWaters}
        subTitle="Fishing spots, fishing reports, and regulations in"
        sourceId={fishingWaterId}
        zoom={11}
      />
      {/* Header Section */}
      <Container className="px-0">
        {!fishingForbidden ? (
          <Grid gapType="custom" className="grid-flow-row gap-0">
            {topSpeciesSummary && hasSpecies && (
              <TopSpeciesSection
                fishingWaterName={displayName}
                fishingWaterId={fishingWaterId}
                topSpeciesSummary={topSpeciesSummary}
                className="order-1 col-span-full mb-10 px-6 sm:mx-16 sm:mb-16 sm:px-8 sm:pt-6 sm:pb-2 md:order-2 md:col-span-5 md:mx-0"
              />
            )}
            <TrophyCatchesAppDownloadSection displayName={displayName} />
            {catchCount > 0 && (
              <FishingReportsGrid
                className="mb-fw order-3 col-span-full md:order-4"
                id={SUB_LINK_IDS.FISHING_REPORTS}
                fakeFilters={top3SpeciesNames}
                fishingReports={recentCatchPostList}
                maxCatchesMobile={FISHINGREPORT_SECTION_MAX_CATCHES_MOBILE}
                maxCatchesTablet={FISHINGREPORT_SECTION_MAX_CATCHES_TABLET}
                maxCatchesDesktop={FISHINGREPORT_SECTION_MAX_CATCHES_DESKTOP}
                sourceId={fishingWaterId}
                title={`${displayName} fishing reports`}
              />
            )}
            {showNoCatchesSection && (
              <NoCatchesSection className="order-1 col-span-full mb-6 sm:mb-10 md:order-2 md:col-span-5" />
            )}
            <GeneralInfoSection
              amenitiesList={amenitiesList}
              className="mb-fw order-3 col-span-full md:order-1 md:col-span-7 md:mt-0 md:pr-5"
              country={country}
              county={county}
              intersectingCounties={intersectingCountiesNodes}
              descriptionOfWaterType={descriptionOfWaterType}
              fishingWaterId={fishingWaterId}
              fishingWaterName={displayName}
              latitude={latitude}
              longitude={longitude}
              region={region}
              metadata={metadata}
              topSpeciesSummary={topSpeciesSummary ?? undefined}
              url={url}
              topAnglers={topAnglers.anglers}
            />
          </Grid>
        ) : (
          <PrivateWaterSection />
        )}
      </Container>
      {/* Bytetime CTA Section */}
      {!fishingForbidden && (
        <BitetimeSection fishingWaterName={displayName} speciesEdges={speciesEdges} />
      )}
      {/* Regulations Section */}
      {!fishingForbidden && showRegulations && (
        <RegulationSection
          className="mb-fw mt-0!"
          coordinates={{ latitude, longitude }}
          topSpeciesId={topSpeciesId[0]}
          region={region ?? undefined}
          title={`Fishing regulations at ${displayName}, ${region?.id}`}
          showLicenceInfo
          id={SUB_LINK_IDS.FISHING_REGULATIONS}
        />
      )}
      {/* Reviews Section */}
      {!fishingForbidden && hasReviews && (
        <ReviewsSection
          allReviews={allReviews}
          fishingWaterName={displayName}
          fishingWaterId={fishingWaterId}
        />
      )}
      {/* Nearby Waters Section */}
      <NearbyWatersSection nearbyWaters={suggestedWaters} />
      {/* Cities nearby Section */}
      {nearbyPlacesArray.length > 0 && (
        <NearbyPlacesSection
          nearbyPlaces={nearbyPlacesArray}
          distanceUnit={nearbyPlacesDistanceUnit}
          mainLatitude={latitude}
          mainLongitude={longitude}
        />
      )}

      {/* Suggest Changes Section */}
      <SuggestChangesSection fishingWaterId={fishingWaterId} displayName={displayName} />
      {/* FAQ Section */}
      {!fishingForbidden && (
        <FAQSection
          county={county}
          country={country}
          descriptionOfWaterType={descriptionOfWaterType}
          intersectingCounties={intersectingCountiesNodes}
          fishingWaterId={fishingWaterId}
          fishingWaterName={displayName}
          latitude={latitude}
          longitude={longitude}
          region={region}
          recentCatchPostList={recentCatchPostList}
          topSpeciesSummary={topSpeciesSummary ?? undefined}
        />
      )}
      {/* AppDownload CTA Section */}
      <AppDownloadSection className="mb-fw py-0!" hasAppRating />
      {/* PrefooterLinks SEO Section */}
      <PreFooterLinksSection country={country} states={states} />
    </div>
  );
};
